import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Account, AccountFilterData, TreeItem, User } from '../../../models';
import { ErrorMessages } from '../../../constants';
import { AuthService } from '../../../services/auth.service';
import { AccountService } from '../../../services';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { delay, isEmptyObject } from '../../../helpers/helpers';

@Component({
  selector: 'app-account-filter',
  standalone: true,
  imports: [
    NgbModule,
    CommonModule,
    FormsModule
  ],
  templateUrl: './account-filter.component.html',
  styleUrl: './account-filter.component.scss'
})
export class AccountFilterComponent implements OnInit {
  @Input() placeholder: string = 'Filter by account';
  @Input() disabled: boolean = false;
  @Input() filter: AccountFilterData;
  @Output() filterChanged: EventEmitter<AccountFilterData> = new EventEmitter<AccountFilterData>();

  @ViewChild('dropdown') dropdown: ElementRef
  @ViewChild('dropdownTrigger') dropdownButton: ElementRef

  isLoading: boolean = false;
  accountName: string = '';

  // accounts
  user: User;
  accounts: Account[] = [];
  accountsTree: TreeItem[] = [];
  filteredTree: TreeItem[] = [];
  searchTerm: string = '';

  // NOTE: This needs to be an array of objects so 
  // it's easier to format selected accounts
  selectedAccount: Array<TreeItem> = [];
  selectedAccountsCount: number = 0;

  constructor(
    private authService: AuthService,
    private accountService: AccountService,
    private toastr: ToastrService) {
  }

  ngOnInit(): void {
    this.init();
  }

  async init(): Promise<void> {
    this.user = this.authService.getUser();
    await this.getUserAccounts();
    await delay(100);
    this.updateFilter();
  }

  openDropdown(event: Event): void {
    event.stopPropagation();

    if (!this.dropdown.nativeElement.className.includes('show')) {
      this.dropdownButton.nativeElement.click();
    }
  }

  async getUserAccounts() {
    if (!!this.user) {
      this.isLoading = true;
      try {
        const response = await this.accountService.getUserAccounts();
        this.applyFilter(response.data!);
        this.setInitialSelectedAccount(response.data!)
        this.accountService.setAccounts(response.data!);
        this.accountsTree = this.createAccountsTree(response.data!);
        this.filteredTree = this.accountsTree;
        this.isLoading = false;
      } catch (error: any) {
        const message = ErrorMessages[error?.error?.errorCode] || ErrorMessages['errors/default'];
        this.toastr.error(message);
        this.isLoading = false;
      }
    }
  }

  setInitialSelectedAccount(accounts: Account[]) {
    if (this.user && accounts && isEmptyObject(this.filter)) {
      this.filter = {
        accountIds: []
      };
    }
  }

  async onCheckboxChange(item: TreeItem & { checked?: boolean }): Promise<void> {
    // toggle the check 
    this.toggleCheckBox(item);
    this.filter.accountIds = this.selectedAccount.map(x => x.id);

    // When all accounts are deselected, reset the filtered tree to show all accounts
    // so the user can see and interact with the full list without needing a page refresh.
    if (this.selectedAccount.length === 0) {
      this.filteredTree = this.accountsTree;
      this.filteredTree.forEach(treeItem => this.applyToggle(treeItem));
    }

    this.updateFilter();
    this.accountName = this.selectedAccount.map(x => x.name).join(', ');
    this.updateSelectedAccountsCount();
  }

  createAccountsTree(accounts: Account[]): TreeItem[] {
    const accountMap = new Map<number, TreeItem>();
    const roots: TreeItem[] = [];

    accounts.forEach(account => {
      const treeItem: TreeItem = { id: account.id, name: account.name, checked: false, hierarchyLevel: account.hierarchyLevel, children: [] };
      this.applyToggle(treeItem);
      accountMap.set(account.id, treeItem);

      if (account.parentId === 0 || account.hierarchyLevel === 0) {
        roots.push(treeItem);
      } else {
        const parent = accountMap.get(account.parentId);
        if (parent) {
          parent.children.push(treeItem);
        }
      }
    });

    return roots;
  }

  searchAccounts(event: Event): void {
    var searchTerm = (event.target as HTMLInputElement).value;
    if (searchTerm) {
      this.filteredTree = this.searchTreeItems(this.accountsTree, searchTerm);
    } else {
      this.filteredTree = this.accountsTree;
    }

    // apply toggle
    this.filteredTree?.forEach(item => {
      this.applyToggle(item);
    });
  }

  searchTreeItems(items: TreeItem[], searchTerm: string): TreeItem[] {
    return items.reduce((acc: TreeItem[], item) => {
      const matchedChildren = this.searchTreeItems(item.children, searchTerm.toLowerCase());

      if (item.name.toLowerCase().includes(searchTerm.toLowerCase()) || matchedChildren.length > 0) {
        acc.push({
          ...item,
          children: matchedChildren
        });
      }

      return acc;
    }, []);
  }

  updateFilter(): void {
    this.filterChanged.next(this.filter);
  }

  private toggleCheckBox(item: TreeItem) {
    if (!this.selectedAccount.find(x => x.id === item.id)) {
      this.selectedAccount.push(item);
      item.checked = true;
    } else {
      this.selectedAccount.splice(this.selectedAccount.findIndex(x => x.id == item.id), 1);
      item.checked = false;
    }

    item.children.forEach(element => {
      this.toggleChildren(element, item.checked);
    });
  }

  private toggleChildren(item: TreeItem, toggle: boolean) {
    if (toggle) {
      if (!this.selectedAccount.find(x => x.id === item.id)) {
        this.selectedAccount.push(item);
        item.checked = true;
      }
    } else {
      this.selectedAccount.splice(this.selectedAccount.findIndex(x => x.id == item.id), 1);
      item.checked = false;
    }

    item.children.forEach(element => {
      this.toggleChildren(element, item.checked);
    });
  }

  private applyToggle(item: TreeItem): void {
    item.checked = !!this.selectedAccount.find(x => x.id == item.id);
    item.children.forEach(child => {
      this.applyToggle(child);
    });
  }

  private applyFilter(accounts: Account[]): void {
    if (this.filter.accountIds && this.filter.accountIds.length > 0) {
      const accountIdsSet = new Set(this.filter.accountIds);
      const filteredAccounts = accounts.filter(x => accountIdsSet.has(x.id));
      this.selectedAccount = filteredAccounts.map<TreeItem>(x => ({
        id: x.id,
        name: x.name,
        hierarchyLevel: x.hierarchyLevel,
        parentId: 0,
        checked: true,
        children: [],
      }));

      this.accountName = this.selectedAccount.map(x => x.name).join(', ');
      this.updateSelectedAccountsCount();
    }
  }

  private updateSelectedAccountsCount(): void {
    this.selectedAccountsCount = this.selectedAccount.length;
  }
}
